import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  UserPlus,
  ShieldCheck,
  Globe,
  Copy,
  Mail,
  Settings,
  MoreVertical,
  Building2,
  Calendar,
  Activity,
  X,
  Check,
  Users,
  Briefcase,
  Clock,
} from 'lucide-react';

export default function CompanyDetails() {
  const { companySlug } = useParams();
  const navigate = useNavigate();

  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const [company] = useState({
    name: (companySlug || 'infosys').toUpperCase(),
    url: `wysele.com/${companySlug || 'infosys'}`,
    status: 'Active',
    createdAt: 'Jan 12, 2025',
    employees: '1,240',
    healthScore: 92,
    activeUsers: '874',
    lastSync: '4 min ago',
    admin: {
      id: 1,
      name: 'Ravi Kumar',
      email: 'ravi.kumar@infosys.com',
      role: 'Super Admin',
      lastActive: '2 hours ago',
    },
  });

  const availableAdmins = [
    { id: 1, name: 'Ravi Kumar', email: 'ravi.kumar@infosys.com' },
    { id: 2, name: 'Anjali Sharma', email: 'a.sharma@global.com' },
    { id: 3, name: 'Mark Benson', email: 'm.benson@ops.com' },
  ];

  const moduleUsage = [
    { name: 'Payroll', value: 88, color: '#4f46e5' },
    { name: 'Leave Mgmt', value: 95, color: '#10b981' },
    { name: 'Recruitment', value: 45, color: '#f59e0b' },
    { name: 'Attendance', value: 72, color: '#ec4899' },
  ];

  const handleAdminChange = (newAdmin) => {
    // In real app → API call here
    setIsAdminPanelOpen(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(company.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto bg-gray-50/40 min-h-screen">
      {/* Header / Breadcrumb */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-indigo-700 transition-colors group"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          Back to Companies
        </button>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold border border-emerald-100/80">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            API • Stable
          </div>
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            <Settings size={18} />
          </button>
        </div>
      </div>

      {/* Company Hero Card */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-6 md:p-8 mb-10 transition-all hover:shadow-md">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl flex items-center justify-center text-indigo-600 border border-indigo-100">
              <Building2 size={40} strokeWidth={1.4} />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{company.name}</h1>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-semibold rounded-full">
                  {company.status}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-600">
                <span className="flex items-center gap-1.5">
                  <Globe size={15} /> {company.url}
                  <button
                    onClick={copyToClipboard}
                    className="ml-1 text-gray-400 hover:text-indigo-600 transition-colors"
                    title="Copy URL"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar size={15} /> Joined {company.createdAt}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={() => window.open(`https://${company.url}`, '_blank')}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-all shadow-sm hover:shadow-md whitespace-nowrap"
          >
            Launch Company Portal <ExternalLink size={16} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column - Main Info + Visuals */}
        <div className="lg:col-span-8 space-y-8">
          {/* Admin Card */}
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-6 transition-all hover:shadow hover:border-indigo-200/60 group">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-sm font-bold uppercase tracking-wide text-gray-800 flex items-center gap-2">
                <ShieldCheck size={16} className="text-indigo-600" /> Primary Super Admin
              </h2>
              <button
                onClick={() => setIsAdminPanelOpen(true)}
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1.5 transition-colors"
              >
                <UserPlus size={14} /> Change
              </button>
            </div>

            <div className="flex items-center gap-4 p-5 bg-gray-50/70 rounded-xl border border-gray-100 group-hover:bg-indigo-50/30 transition-colors">
              <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xl shadow-sm">
                {company.admin.name.charAt(0)}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{company.admin.name}</p>
                <p className="text-sm text-gray-600 mt-0.5">{company.admin.email}</p>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Active</div>
                <div className="text-xs text-gray-500 mt-1">Last seen: {company.admin.lastActive}</div>
              </div>
            </div>
          </div>

          {/* Health & Utilization Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Health Score – Circular Progress */}
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-6 transition-all hover:shadow-md hover:border-indigo-200/50 group">
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-800 mb-5 flex items-center gap-2">
                <Activity size={16} className="text-indigo-600" /> System Health
              </h3>
              <div className="flex items-center gap-6">
                <div className="relative w-24 h-24">
                  <svg className="w-full h-full -rotate-90">
                    <circle cx="48" cy="48" r="40" stroke="#f3f4f6" strokeWidth="12" fill="none" />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="#4f46e5"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={251}
                      strokeDashoffset={251 - (251 * company.healthScore) / 100}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-gray-800">{company.healthScore}%</span>
                    <span className="text-xs text-gray-500">Healthy</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700">Last sync: {company.lastSync}</div>
                  <div className="text-xs text-gray-500 mt-1">Uptime • 99.94%</div>
                </div>
              </div>
            </div>

            {/* License Utilization – Progress + Mini Stats */}
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-6 transition-all hover:shadow-md hover:border-indigo-200/50 group">
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-800 mb-5 flex items-center gap-2">
                <Users size={16} className="text-indigo-600" /> License Usage
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{company.employees} / 2,300 seats</span>
                  <span className="font-bold text-indigo-700">54%</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-500 to-blue-600 transition-all duration-500 group-hover:scale-x-105 origin-left"
                    style={{ width: '54%' }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Active users: {company.activeUsers}</span>
                  <span>Idle: ~366</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column – Module Usage */}
        <div className="lg:col-span-4">
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-6 h-fit transition-all hover:shadow-md hover:border-indigo-200/50 sticky top-6">
            <h2 className="text-sm font-bold uppercase tracking-wide text-gray-800 mb-6 flex items-center gap-2">
              <Briefcase size={16} className="text-indigo-600" /> Module Adoption
            </h2>

            <div className="space-y-6">
              {moduleUsage.map((mod) => (
                <div key={mod.name} className="group">
                  <div className="flex justify-between text-xs font-medium mb-2 text-gray-700">
                    <span>{mod.name}</span>
                    <span>{mod.value}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-400 group-hover:scale-x-105 origin-left"
                      style={{
                        width: `${mod.value}%`,
                        background: `linear-gradient(to right, ${mod.color}, ${mod.color}88)`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Small pie-like summary */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-600 mb-4 uppercase tracking-wide">Overall Adoption</p>
              <div className="flex items-center gap-4">
                <div className="relative w-20 h-20">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    {moduleUsage.reduce(
                      (acc, mod, i) => {
                        const prevOffset = acc.prevOffset;
                        const dash = (mod.value / 100) * 251;
                        acc.segments.push(
                          <circle
                            key={i}
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke={mod.color}
                            strokeWidth="16"
                            strokeDasharray={`${dash} 251`}
                            strokeDashoffset={-prevOffset}
                          />,
                        );
                        acc.prevOffset += dash;
                        return acc;
                      },
                      { segments: [], prevOffset: 0 },
                    ).segments}
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#f3f4f6" strokeWidth="16" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-gray-700">
                    75%
                  </div>
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  <div>4 active modules</div>
                  <div className="text-indigo-600 font-medium">Recruitment needs attention</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide-over Admin Change Panel */}
      {isAdminPanelOpen && (
        <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex justify-end">
          <div className="w-full max-w-md bg-white h-full shadow-2xl animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg text-gray-900">Change Super Admin</h3>
              <button
                onClick={() => setIsAdminPanelOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">
                Available administrators
              </p>

              {availableAdmins.map((adm) => (
                <div
                  key={adm.id}
                  onClick={() => handleAdminChange(adm)}
                  className={`p-4 border rounded-xl cursor-pointer transition-all flex items-center justify-between ${
                    company.admin.id === adm.id
                      ? 'border-indigo-500 bg-indigo-50/60'
                      : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 font-medium">
                      {adm.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-medium">{adm.name}</div>
                      <div className="text-xs text-gray-500">{adm.email}</div>
                    </div>
                  </div>
                  {company.admin.id === adm.id && <Check size={18} className="text-indigo-600" />}
                </div>
              ))}
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gray-50 border-t text-center">
              <p className="text-xs text-gray-500 italic">
                Permission transfer is immediate and cannot be undone
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}